import {
  detectPaginationScheme,
  getGraphQLQueryString,
  getIndexingQueryString,
  prettifyQuery
} from '@takeshape/graphql';
import {
  ensureQuery,
  findQueryByShape,
  hasArg,
  isCachedShape,
  type ListQueryIndexingConfig,
  type ProjectSchemaJSON
} from '@takeshape/schema';
import type { Maybe } from '@takeshape/util';

// Get queryName for a shapeName
export function getShapeQueryName(schema: ProjectSchemaJSON, shapeName?: string): Maybe<string> {
  if (!shapeName) return;
  const shape = schema.shapes[shapeName];
  if (shape) {
    if (shape.model) {
      const isSingleton = shape.model?.type === 'single';
      const resolver = isSingleton ? 'shapedb:get' : 'shapedb:list';
      return findQueryByShape(schema, shapeName, resolver);
    }

    if (isCachedShape(shape)) {
      return findQueryByShape(schema, shapeName, 'takeshape:queryApiIndex');
    }
  }
}

export function getQueryString(schema: ProjectSchemaJSON, queryRef?: string, id?: string): string {
  let queryString = '';
  if (queryRef) {
    try {
      const { query } = ensureQuery(schema, queryRef);
      const args: Record<string, unknown> = {};
      if (id !== undefined) {
        if (hasArg(schema, query, '_id')) {
          args._id = id;
        } else if (hasArg(schema, query, 'id')) {
          args.id = id;
        }
      }

      // Make previewing relay paginated queries one click
      const pagination = detectPaginationScheme(schema, query);
      if (pagination?.type === 'cursor') {
        args[pagination.pageSizeArg] = 5;
      }

      if ('name' in query.resolver && query.resolver.name === 'takeshape:queryApiIndex' && query.resolver.shapeName) {
        const shapeName = query.resolver.shapeName;
        const shape = schema.shapes[shapeName];

        if (!shape.cache) {
          console.warn(`${queryRef} uses the takeshape:queryApiIndex resolver but the shape is not cached.`);
          return '';
        }

        const queryConfig: ListQueryIndexingConfig = {
          query: queryRef,
          shapeName: shape.name,
          idField: shape.cache.idField ?? '_id',
          ...shape.cache.fragment
        };

        queryString = prettifyQuery(getIndexingQueryString(schema, queryConfig, ['items'], {}));
      } else {
        queryString = prettifyQuery(
          getGraphQLQueryString(schema, queryRef, {
            args,
            onlyFormProps: true,
            varsForMissing: true,
            maxQueryDepth: 2
          })
        );
      }
    } catch {
      // If we encounter an error while getting the query string or prettifying it,
      // we want to handle it by letting the function return an empty string
    }
  }

  return queryString;
}
